"use client";

import ContentContainer from "@/components/ContentContainer/ContentContainer";
import { MODAL_CONTENT } from "@/constants/modalContent";
import cn from "@/lib/cn";
import dynamic from "next/dynamic";
import React, { useState } from "react";
import type { BentoCarouselContainerProps } from "../../types";
import BentoTab from "../BentoTab/BentoTab";
import bentoTabStyles from "../BentoTab/BentoTab.module.css";
import CarouselHeader from "./CarouselHeader";

const Modal = dynamic(() => import("@/components/Modal"), { ssr: false });

/**
 * Pure UI component for Bento Carousel
 *
 * This component:
 * 1. Renders header, carousel, and modal
 * 2. Handles button clicks and modal state
 * 3. No knowledge of Monarch - receives transformed props
 */
const BentoCarouselContainer: React.FC<BentoCarouselContainerProps> = ({
  title = "All internet plans include:",
  subtitle,
  cards = [],
  onButtonClick,
  enableContentZoom,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModalContent, setActiveModalContent] = useState<string | null>(
    null
  );

  const handleButtonClick = (action: string) => {
    console.log(`Button clicked with action: ${action}`);

    // Check if this action corresponds to modal content
    if (MODAL_CONTENT[action]) {
      setActiveModalContent(action);
      setIsModalOpen(true);
    } else if (onButtonClick) {
      onButtonClick(action);
    } else {
      console.log(`Non-modal action: ${action}`);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveModalContent(null);
  };

  const modalData = activeModalContent
    ? MODAL_CONTENT[activeModalContent]
    : null;

  return (
    <>
      <ContentContainer className="bg-white max-w-full md:max-w-5xl lg:max-w-384 !px-6 md:!px-8 lg:!px-16">
        <CarouselHeader title={title} subtitle={subtitle} />

        <BentoTab
          id="internet"
          cards={cards}
          layoutType="4-cards-equal"
          enableCarousel={true}
          onButtonClick={handleButtonClick}
          sliderClassName={cn(
            "md:px-0",
            bentoTabStyles.hideInactiveSlidesOnTablet
          )}
          tabType="internet"
          enableContentZoom={enableContentZoom}
        />
      </ContentContainer>

      {modalData ? (
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={modalData.title}
          tagularProps={{
            name: `modal${modalData.elementName}`,
          }}
        >
          {modalData.content}
        </Modal>
      ) : null}
    </>
  );
};

export default BentoCarouselContainer;
